import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
  RefObject,
} from 'react';
import { Control, useController, useFormState } from 'react-hook-form';
import ReactQuill from 'react-quill-new';
// Quill's stylesheet is loaded by the RichTextEditor module imported below.

import { Typography } from '@mui/material';
import { FlexContainer } from '@act/storybook';
import { NoteItemType } from '@act/shared/data-transfer-objects';
import { removeHtmlTags } from '@act/shared/utilities';

import SaveStatusIndicator from 'toolkit/inputs/SaveStatusIndicator';
import { themeColors } from 'toolkit/styles/colors';
import { useTracking } from '../tracking';
import { RTEContainer, formats, getModules } from './RichTextEditor';
import NoteRecoveryDialog from './components/NoteRecoveryDialog';
import useAutosave, {
  AutosaveFlushResult,
  isEmptyNoteHtml,
} from './hooks/useAutosave.hook';
import useNoteDraft from './hooks/useNoteDraft.hook';
import useNoteForItem from './hooks/useNoteForItem.hook';

/**
 * Lets a caller route the save through its own request — e.g. write through a
 * parent entity (updateCall) so derived flags like `insufficientNotes` stay in
 * sync — instead of NoteField's default unified `/notes` read/write.
 */
export interface NoteFieldSaveOverride {
  onSave: (text: string) => void;
  isSaving?: boolean;
  error?: unknown;
}

export interface NoteFieldProps {
  /** RHF control from the parent form. */
  control: unknown;
  /** Field name within the parent form. */
  name: string;
  /** 'create' uses the autosave draft; 'edit' reads/writes the unified note. */
  mode?: 'create' | 'edit';
  itemType: NoteItemType;
  /** Parent item id (required for edit unless `save` is provided). */
  itemId?: string;
  /** Known note id from the parent payload (e.g. `visit.noteId`) — skips the by-item lookup. */
  noteId?: number;
  /** Member the note belongs to. */
  userId: string;
  /** Draft bucket for create flows (defaults to 'notes'). */
  draftType?: string;
  draftId?: string;
  /**
   * Create-mode editor baseline when the user discards a recovered draft
   * (e.g. LLM-seeded suggestion text).
   */
  draftDiscardBaseline?: string;
  /** Notifies parent dialogs when a recovered draft tag should appear in the header. */
  onRecoveredNoteChange?: (isRecovered: boolean) => void;
  readOnly?: boolean;
  placeholderText?: string;
  trackingName?: string;
  height?: string | number;
  /** When set, shown in the character count as "• N minimum required". */
  characterMinimum?: number;
  showCharacterCount?: boolean;
  save?: NoteFieldSaveOverride;
  ref?: RefObject<NoteFieldRef>;
}

export interface NoteFieldRef {
  /** Persist the latest value immediately (e.g. before closing the dialog). */
  flush: () => void;
  /**
   * Like `flush`, but resolves once the autosave request settles. Use before a
   * full parent save so debounced note PATCHes cannot race the save.
   */
  flushAndWait: () => Promise<AutosaveFlushResult>;
  /** Discard the create-flow autosave draft (e.g. after a successful create). */
  clearDraft: () => void;
}

/**
 * Reusable note editor with built-in autosave, save-status feedback, and draft
 * recovery — the single building block for every note surface (visit, call,
 * member, scheduled event, hospitalization, survey).
 */
const NoteField = forwardRef<NoteFieldRef, NoteFieldProps>(
  (
    {
      control,
      name,
      mode = 'edit',
      itemType,
      itemId,
      noteId,
      userId,
      draftType = 'notes',
      draftId,
      draftDiscardBaseline,
      onRecoveredNoteChange,
      readOnly = false,
      placeholderText = 'Add notes here...',
      trackingName,
      height,
      showCharacterCount = false,
      save,
      characterMinimum = 0,
    },
    ref
  ) => {
    const isOverride = !!save;
    const isCreate = mode === 'create';
    const rhfControl = control as Control;

    const { field, fieldState } = useController({
      control: rhfControl,
      name,
    });
    const { isSubmitted } = useFormState({ control: rhfControl });
    const value: string = field.value ?? '';

    const item = useNoteForItem({
      itemType,
      itemId: itemId ?? '',
      userId,
      noteId,
      enabled: !isOverride && !isCreate && !!itemId,
    });
    const draft = useNoteDraft({
      subjectId: userId,
      type: draftType,
      draftId,
      enabled: !isOverride && isCreate,
    });

    const [recoveryResolved, setRecoveryResolved] = useState(false);
    const [isRecoveredNote, setIsRecoveredNote] = useState(false);

    useEffect(() => {
      setRecoveryResolved(false);
      setIsRecoveredNote(false);
    }, [draftId, draftType, userId, mode]);

    useEffect(() => {
      onRecoveredNoteChange?.(isRecoveredNote);
    }, [isRecoveredNote, onRecoveredNoteChange]);

    const draftText = draft.draft?.text;
    const hasRecoverableDraft =
      !!draftText && !isEmptyNoteHtml(draftText) && draftText !== value;
    const showRecovery =
      isCreate &&
      !isOverride &&
      !recoveryResolved &&
      !draft.isLoading &&
      hasRecoverableDraft;

    const onSave = save?.onSave ?? (isCreate ? draft.saveDraft : item.saveText);
    const isSaving =
      save?.isSaving ?? (isCreate ? draft.isSaving : item.isSaving);
    const saveError =
      save?.error ?? (isCreate ? draft.saveError : item.saveError);

    const autosave = useAutosave({
      value,
      onSave,
      isSaving,
      error: saveError,
      // Pause autosave until the recovery prompt is resolved.
      enabled: !readOnly && !showRecovery,
    });

    const resolvedItemNote = item.note;
    useEffect(() => {
      if (isOverride || isCreate || item.isLoading || !resolvedItemNote) {
        return;
      }
      const canonicalText = resolvedItemNote.text ?? '';
      if (!autosave.isDirty && canonicalText !== value) {
        field.onChange(canonicalText);
        autosave.markSaved(canonicalText);
      }
    }, [
      isOverride,
      isCreate,
      item.isLoading,
      resolvedItemNote?.id,
      resolvedItemNote?.text,
      autosave.isDirty,
      autosave.markSaved,
      value,
      field,
    ]);

    useImperativeHandle(
      ref,
      () => ({
        flush: autosave.flush,
        flushAndWait: autosave.flushAndWait,
        clearDraft: draft.clearDraft,
      }),
      [autosave.flush, autosave.flushAndWait, draft.clearDraft]
    );

    const handleContinueRecovery = () => {
      const recoveredText = draftText ?? '';
      field.onChange(recoveredText);
      // Draft already lives on the server — skip the debounced re-save on enable.
      autosave.markSaved(recoveredText);
      setRecoveryResolved(true);
      setIsRecoveredNote(true);
    };
    const handleDiscardRecovery = () => {
      draft.clearDraft();
      const baseline =
        draftDiscardBaseline && !isEmptyNoteHtml(draftDiscardBaseline)
          ? draftDiscardBaseline
          : '<p><br></p>';
      field.onChange(baseline);
      setRecoveryResolved(true);
    };

    const quillModules = useMemo(() => getModules(readOnly), [readOnly]);
    const { generateTrackingProps } = useTracking();

    const characterCount = useMemo(
      () => (showCharacterCount ? removeHtmlTags(value).length : 0),
      [value, showCharacterCount]
    );
    const hasCharacterMinimum = characterMinimum > 0;
    const isBelowCharacterMinimum =
      hasCharacterMinimum && characterCount < characterMinimum;
    const showMinimumError = isSubmitted && isBelowCharacterMinimum;
    const characterCountLabel = `${characterCount} ${
      characterCount === 1 ? 'character' : 'characters'
    }`;
    const characterCountText = hasCharacterMinimum
      ? `${characterCountLabel} • ${characterMinimum} minimum required`
      : characterCountLabel;

    return (
      <FlexContainer direction="column" gap={2} width="100%" height="auto">
        {!readOnly && (
          <FlexContainer
            direction="row"
            justifyContent="flex-end"
            alignItems="center"
          >
            <SaveStatusIndicator status={autosave.status} noteType={itemType} />
          </FlexContainer>
        )}

        <RTEContainer $height={height}>
          <div
            {...(generateTrackingProps && trackingName
              ? generateTrackingProps(trackingName)
              : {})}
          >
            <ReactQuill
              value={value}
              onChange={(content: string) => field.onChange(content)}
              readOnly={readOnly}
              modules={quillModules}
              formats={formats}
              placeholder={placeholderText}
            />
          </div>
        </RTEContainer>

        {fieldState.error && (
          <Typography variant="bodySmall" color={themeColors.error}>
            {fieldState.error.message}
          </Typography>
        )}

        {showCharacterCount && (
          <Typography
            variant="bodySmall"
            color={showMinimumError ? themeColors.error : undefined}
          >
            {characterCountText}
          </Typography>
        )}

        {showRecovery && draftText && (
          <NoteRecoveryDialog
            open={showRecovery}
            draftText={draftText}
            onContinue={handleContinueRecovery}
            onDiscard={handleDiscardRecovery}
          />
        )}
      </FlexContainer>
    );
  }
);

NoteField.displayName = 'NoteField';
export default NoteField;
